<template>
  <div style="height:100%;">
    <commodity-analysis
      :Analytics="Analytics"
      :asset="asset"
      :colorTheme="colorTheme"
      :dataNest="dataNest"
      :idChart="idChart"
      :infoCharts="infoCharts"
      :infoLegends="infoLegends"
      :isPrices="isPrices"
      :isZoomY="isZoomY"
      :loadingAll="loadingAll"
      :processData="processData"
      :renderChartSignal="renderChartSignal"
      @eventOnUserShow="onUserShow"
      @onTableFormat="onHoverChart"
    />
  </div>
</template>

<script>
import CommodityAnalysis from "./CommodityAnalysis.vue";
import { defaultAnalytics } from "@/_core/data/Analytics/AnalyticsHelpers.js";
import {
  formatM,
  formatPercentSm,
  colorExpo,
  colorExpoAlpha4,
  colorFwd,
  colorFwdAlpha2,
  colorHedge,
  colorHedgeAlpha,
  colorNet,
  colorNetAlpha2,
  currencyFormat3,
  whiteGray,
  whiteGrayAlpha3,
} from "@/_core/_helpers/_parametric_tools";
import { textactive } from "@/_core/_helpers/_parametric_tools";

export default {
  components: { CommodityAnalysis },
  name: "CommodityPortfolioScenarios",
  props: {
    Analytics: {
      required: true,
      default: defaultAnalytics(),
    },
    asset: { required: false, default: "commodity" },
    colorTheme: { required: true, type: Boolean, default: true },
    idChart: { required: true },
    isPrices: { default: false },
    loadingAll: { required: true, type: Boolean, default: false },
    processData: { required: true },
    renderChartSignal: { required: true, default: 0 },
  },
  data() {
    return {
      dataNest: "zoomedData",
      isZoomY: true,
      formatVars: this.getUpdatedFormatVars(),
      infoCharts: [
        {
          label: "",
          nameEvent: "",
          nameData: "",
          colorBdr: "",
          colorBck: "",
          classNameIcon: "",
          show: "",
        },
      ],
      infoLegends: [
        {
          label: "",
          show: "",
          classNameIcon: "",
          colorBdr: "",
          colorBck: "",
        },
      ],
      labels: {
        difference: "Net - Mkt",
        differenceOne: "Hedge P&L",
        differenceTwo: "Open Exposure",
        differenceThree: "Net - Budget",
      },
    };
  },
  created() {
    this.getInfoCharts();
    this.updateFormatVars();
  },
  watch: {
    "Analytics.userSelections": {
      handler() {
        this.updateTableInfo();
      },
      deep: true,
    },
    colorTheme() {
      this.updateFormatVars();
      this.getInfoCharts();
    },
    showAve() {
      this.updateMethodsFormatVars();
      this.getInfoCharts();
    },
  },
  methods: {
    getUpdatedFormatVars() {
      return {
        budgetColor: colorExpo,
        budgetBColor: colorExpoAlpha4,
        fixColor: colorFwd,
        fixBColor: colorFwdAlpha2,
        mktColor: whiteGray,
        mktBColor: whiteGrayAlpha3,
        netColor: colorNet,
        netBColor: colorNetAlpha2,
        ratioColor: colorHedge,
        ratioBColor: colorHedgeAlpha,
        userHovered: {
          date: "",
          variable: "",
          hovered: false,
          fromChart: false,
        },
      };
    },
    updateFormatVars() {
      this.formatVars = this.getUpdatedFormatVars();
    },
    getInfoCharts() {
      const allUniqueKeys = this.extractUniqueKeys();
      console.log("allUniqueKeys", allUniqueKeys);
      const dataUnitConfig = this.createDataUnitConfig();
      console.log("dataUnitConfig", dataUnitConfig);

      // Process derivatives and chart information
      const derivatives = this.processDerivatives(dataUnitConfig);
      console.log("derivatives", derivatives);
      const chartInfo = this.processChartInfo(
        allUniqueKeys,
        dataUnitConfig,
        derivatives,
      );
      console.log("chartInfo", chartInfo);

      // Update analytics and emit events
      this.updateAnalyticsData(chartInfo);
      this.emitTableInfoChange();
      this.infoLegends = this.createInfoLegends(this.infoCharts);
      console.log("infoLegends", this.infoLegends);
    },
    createDataObject(
      key,
      dataUnitConfig,
      methodPrefix = key,
      isDerivative = true,
      format = formatM,
    ) {
      const method = this[methodPrefix]();
      if (!method) return null;

      const {
        label,
        show,
        colorBck,
        colorBdr,
        typeFigure,
        oneYField,
        twoYField,
        oneStackedBar,
        twoStackedBar,
        figureDetails,
        height,
        xOffsetLeft,
        xOffsetRight,
        showText,
        figureElements,
        colorBckTable,
        classNameIcon,
        unitPerc,
        showArr,
        isDetails,
        customYFieldsText,
        nameData,
        nameEvent,
        isDerivativeAve,
        labelTable,
      } = method;

      // Update user selections for non-derivative items
      if (!isDerivative) {
        Object.assign(this.Analytics.userSelections, { [show]: true });
      }

      return {
        label,
        labelTable: labelTable || label,
        nameEvent: nameEvent || key.split(" ").join(""),
        elementName: isDerivative ? "" : key.split(" ").join(""),
        nameData: nameData || key,
        classNameIcon: this.isPrices && classNameIcon,
        colorBdr,
        colorBck: this.Analytics.userSelections[show]
          ? colorBck
          : "var(--transparent)",
        colorBckTable,
        unitPerc: isDerivative ? "" : unitPerc || `${key}Perc`,
        show,
        formatF: format,
        formatPerc: formatPercentSm,
        isPrices: this.isPrices,
        typeFigure,
        oneYField: oneYField || "",
        height: height || 2,
        twoYField: twoYField || "",
        oneStackedBar: oneStackedBar || "",
        twoStackedBar: twoStackedBar || "",
        showArr: showArr || "",
        figureElements: isDerivative
          ? ""
          : this.isPrices
          ? "bar"
          : figureElements,
        figureDetails: figureDetails || { toZero: false },
        showText: showText || false,
        isDetails,
        xOffsetLeft: xOffsetLeft || 0,
        xOffsetRight: xOffsetRight || 0,
        customYFieldsText: customYFieldsText || null,
        strokeWidth:
          typeFigure.nameFigure === "gBar" && !typeFigure.toZero ? 2 : 1,
        isDerivativeAve,
        ...dataUnitConfig,
      };
    },
    extractUniqueKeys() {
      const allUniqueKeys = [];
      const addToAllUniqueKeys = (key) => {
        if (!allUniqueKeys.includes(key)) {
          allUniqueKeys.push(key);
        }
      };

      this.Analytics.rawData[this.dataNest].forEach((d) => {
        const keys = Object.keys(d);
        keys.forEach(addToAllUniqueKeys);
      });

      return allUniqueKeys;
    },
    createDataUnitConfig() {
      const dataUnitConfig = {};
      const userSelectedAsset = this.Analytics.userSelections.selectedAsset[0];

      if (!this.isCurrency) {
        dataUnitConfig["sUnit"] = userSelectedAsset.fk_idcurrency;
        dataUnitConfig["unit"] = userSelectedAsset.fk_idunit;
      } else {
        const currName = userSelectedAsset.currency_name;
        dataUnitConfig["sUnit"] = currName.slice(0, 3);
        dataUnitConfig["unit"] = currName.slice(3);
      }

      dataUnitConfig[`div${this.isPrices ? "P" : "Q"}`] = this.isPrices;
      return dataUnitConfig;
    },
    processDerivatives(dataUnitConfig) {
      const dataDerivatives = [];

      // Handle multiple properties for rates chart
      if (this.hasMultipleProperties && this.idChart === "rates") {
        dataDerivatives.push(
          this.createDataObject("allDerivatives", dataUnitConfig),
        );
        dataDerivatives.push(
          this.createDataObject("allDerivativesAve", dataUnitConfig),
        );
      }

      // Process price-related derivatives
      if (this.isPrices && this.idChart === "rates") {
        const derivativeNames = [
          "longfuture",
          "shortfuture",
          "longcall",
          "shortcall",
          "longput",
          "shortput",
        ];

        derivativeNames.forEach((derivativeName) => {
          const derivativeData = this.createDataObject(
            derivativeName,
            dataUnitConfig,
          );
          if (derivativeData) {
            dataDerivatives.push(derivativeData);
          }
        });
      }

      return dataDerivatives;
    },
    emitTableInfoChange() {
      const changeInfoTable = this.Analytics.tablep_zoomedData?.concat(
        this.Analytics.tableq_zoomedData,
      );
      this.$emit("changeInfoTable", changeInfoTable || [{}]);
    },
    processChartInfo(allUniqueKeys, dataUnitConfig, derivatives) {
      const isPricesChart = this.isPrices ? "p_" : "q_";

      // Filter and map chart keys
      const chartInfo = allUniqueKeys
        .filter(
          (key) =>
            key.startsWith(isPricesChart) &&
            !key.endsWith("Perc") &&
            !key.endsWith("PercS"),
        )
        .map((key) => {
          const methodPrefix = key
            .replace(isPricesChart, "")
            .split(" ")
            .join("");

          return this.createDataObject(
            key,
            dataUnitConfig,
            methodPrefix,
            false,
          );
        });

      // Combine chart info with derivatives
      this.infoCharts.splice(
        0,
        this.infoCharts.length,
        ...chartInfo.concat(derivatives),
      );
      const chartInfoTable = chartInfo;

      // Add difference row for non-price charts
      if (!this.isPrices) {
        const differences = [
          { label: this.labels.difference, nameMethod: "difference" },
          { label: this.labels.differenceOne, nameMethod: "differenceOne" },
          { label: this.labels.differenceTwo, nameMethod: "differenceTwo" },
          { label: this.labels.differenceThree, nameMethod: "differenceThree" },
        ];
        differences.forEach((diff) => {
          const { result, isRatio } = this.setRowDifference(diff.label);
          if (result) {
            const format = this.isFormatCurr(isRatio);
            chartInfoTable.push(
              this.createDataObject(
                result,
                dataUnitConfig,
                diff.nameMethod,
                false,
                format,
              ),
            );
          }
        });
      }
      return chartInfoTable;
    },
    updateAnalyticsData(chartInfoTable) {
      console.log(
        "updateAnalyticsData",
        `table${this.isPrices ? "p_" : "q_"}${this.dataNest}`,
      );
      Object.assign(this.Analytics, {
        [`table${this.isPrices ? "p_" : "q_"}${this.dataNest}`]: chartInfoTable,
      });
    },
    createInfoLegends(infoCharts) {
      return infoCharts.map((chart) => ({
        label: chart.label,
        show: chart.show,
        classNameIcon: chart.classNameIcon,
        colorBdr: chart.colorBdr,
        colorBck: chart.colorBck,
      }));
    },
    getConfig({
      label,
      colorBdr,
      colorBck,
      show,
      typeFigure = "",
      figureDetails = "",
      xOffsetLeft = 0,
      showText = false,
      figureElements,
      colorBckTable = colorBck,
      classNameIcon = "fa fa-minus",
      unitPerc = "",
      showArr = "",
      isDetails = false,
      nameData = undefined,
      nameEvent = undefined,
      isDerivative = false,
      labelTable = undefined,
    }) {
      return {
        label,
        colorBdr,
        colorBck,
        show,
        typeFigure,
        figureDetails,
        xOffsetLeft,
        showText,
        figureElements,
        colorBckTable,
        unitPerc,
        classNameIcon,
        showArr,
        isDetails,
        nameData,
        nameEvent,
        isDerivativeAve: isDerivative,
        labelTable,
      };
    },
    updateMethodsFormatVars() {
      this.budget();
      this.forecast();
      this.market();
      this.ratio();
      this.fixed();
      this.net();
      this.difference();
      this.differenceOne();
      this.differenceTwo();
      this.differenceThree();
    },
    budget() {
      return this.getConfig({
        label: "Budget",
        colorBdr: this.formatVars["budgetColor"](this.colorTheme),
        colorBck: textactive(!this.colorTheme),
        show: "showBudget",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.9, offset: 0, height: 4, strokeWidth: 2 },
        xOffsetLeft: 0,
        showText: false,
        figureElements: "bar",
        colorBckTable: this.formatVars["budgetBColor"](this.colorTheme),
        unitPerc: "budgetQPerc",
        isDetails: !this.isPrices,
      });
    },
    forecast() {
      return this.getConfig({
        label: "Forecast",
        colorBdr: this.isPrices ? "var(--expo)" : "var(--symmetry)",
        colorBck: "var(--symmetry-alpha)",
        show: "showForecast",
        typeFigure: {
          nameFigure: "gBar",
          toZero: !this.isPrices,
        },
        figureDetails: { barWidth: 0.75, offset: 0, height: 4, strokeWidth: 2 },
        xOffsetLeft: 0,
        showText: false,
        figureElements: "bar",
        colorBckTable: this.formatVars["budgetBColor"](this.colorTheme),
        unitPerc: "-",
      });
    },
    market() {
      return this.getConfig({
        label: "Market",
        colorBdr: "var(--whiteGray)",
        colorBck: this.formatVars["mktBColor"](this.colorTheme),
        show: "showMarket",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.85, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        figureElements: "bar",
        unitPerc: this.isPrices && "marketPPerc",
      });
    },
    ratio() {
      return this.getConfig({
        label:
          this.Analytics["assetType"] === "commodity"
            ? "Basis/Ratio"
            : "Recognized",
        colorBckTable: this.formatVars["ratioBColor"](this.colorTheme),
        colorBdr: this.formatVars["ratioColor"](this.colorTheme),
        colorBck: "var(--hedge-alpha)",
        show: "showFixedBasis",
        showArr: ["showFixedBasis", "includeRatio"],
        typeFigure: {
          nameFigure: "gBar",
          toZero: !this.isPrices,
        },
        figureDetails: {
          barWidth: this.isPrices ? 0.65 : 0.25,
          offset: this.isPrices ? 0 : 0.24,
          strokeWidth: 2,
          height: 4,
        },
        xOffsetLeft: -2,
        showText: true,
        figureElements: "bar",
        unitPerc: this.isPrices ? "ratioPPerc" : "ratioQPerc",
      });
    },
    fixed() {
      return {
        ...this.getConfig({
          label: this.isPrices ? "Hedge" : "Fixed",
          labelTable: "Fixed",
          colorBckTable: this.formatVars["fixBColor"](this.colorTheme),
          colorBdr: this.formatVars["fixColor"],
          colorBck: "var(--fwd-alpha)",
          show: "showFixedFutures",
          showArr: ["showFixedFutures", "includeFutures"],
          typeFigure: this.isPrices
            ? { nameFigure: "gBar", toZero: false }
            : { nameFigure: "twoStackedBars", toZero: true },
          figureDetails: {
            barWidth: this.isPrices ? 0.55 : 0.25,
            offset: this.isPrices ? 0 : 0.51,
            strokeWidth: 2,
            height: 4,
          },
          xOffsetLeft: +2,
          showText: false,
          figureElements: "stacked1Bar",
          unitPerc: this.isPrices ? "fixedPPerc" : "fixedQPerc",
        }),
        oneStackedBar: "q_fixed",
      };
    },
    net() {
      return this.getConfig({
        label: "Net",
        colorBdr: this.formatVars["netColor"](this.colorTheme),
        colorBck: this.formatVars["netBColor"](this.colorTheme),
        show: "showNet",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.45, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        figureElements: "bar",
        unitPerc: this.isPrices && "netPPerc",
      });
    },
    hedged() {
      return {
        ...this.getConfig({
          label: "Options",
          colorBdr: "var(--net_2)",
          colorBck: "var(--net-alpha_2)",
          show: "showFixedFutures",
          typeFigure: { nameFigure: "twoStackedBars", toZero: true },
          figureDetails: null,
          xOffsetLeft: +2,
          showText: true,
          figureElements: "stacked2Bar",
          colorBckTable: "var(--net-alpha-2)",
        }),
        twoStackedBar: "q_hedged",
        customYFieldsText: [`q_fixed`, `q_hedged`],
      };
    },
    difference() {
      return {
        ...this.getConfig({
          label: this.labels.difference,
          colorBckTable: this.formatVars["mktBColor"](this.colorTheme),
          colorBdr: "var(--whiteGray)",
          colorBck: this.formatVars["mktBColor"](this.colorTheme),
          figureElements: "",
        }),
      };
    },
    differenceOne() {
      return {
        ...this.getConfig({
          label: this.labels.differenceOne,
          colorBckTable: this.formatVars["fixBColor"](this.colorTheme),
          colorBdr: this.formatVars["fixColor"],
          colorBck: "var(--fwd-alpha)",
          figureElements: "",
          isDetails: true,
        }),
      };
    },
    differenceTwo() {
      return {
        ...this.getConfig({
          label: this.labels.differenceTwo,
          colorBckTable: this.formatVars["ratioBColor"](this.colorTheme),
          colorBdr: this.formatVars["ratioColor"](this.colorTheme),
          colorBck: "var(--hedge-alpha)",
          figureElements: "",
          isDetails: true,
        }),
      };
    },
    differenceThree() {
      return {
        ...this.getConfig({
          label: this.labels.differenceThree,
          colorBckTable: this.formatVars["budgetBColor"](this.colorTheme),
          colorBdr: this.formatVars["budgetColor"](this.colorTheme),
          colorBck: textactive(!this.colorTheme),
          figureElements: "",
          isDetails: true,
        }),
      };
    },
    isFormatCurr(value) {
      return value ? currencyFormat3 : formatM;
    },
    // Validate user selection of Futures: Ratio: Basis
    setRowDifference(differenceType) {
      const {
        includeFutures,
        includeBasis,
        includeRatio,
      } = this.Analytics.userSelections;

      // Futures: Ratio: Basis
      if (includeFutures && includeBasis && includeRatio) {
        return this.getFuturesRatioBasisResult(differenceType);
        // Futures Ratio
      } else if (includeRatio && includeFutures) {
        return this.getFuturesRatioResult(differenceType);
        // Futures
      } else if (includeFutures) {
        return this.getFuturesResult(differenceType);
        // Basis
      } else if (includeBasis) {
        return this.getBasisResult(differenceType);
        // ratio
      } else if (includeRatio) {
        return this.getRatioResult(differenceType);
      }
      return { result: "", ratio: false };
    },
    getFuturesRatioBasisResult(differenceType) {
      switch (differenceType) {
        case this.labels.differenceOne:
          return { result: "v_fixed_hedged", isRatio: false };
        case this.labels.differenceTwo:
          return { result: "v_exposed", isRatio: false };
        case this.labels.differenceThree:
          return { result: "v_budget", isRatio: false };
        default:
          return { result: "v_p", isRatio: false };
      }
    },
    getFuturesRatioResult(differenceType) {
      switch (differenceType) {
        case this.labels.differenceOne:
          return { result: "v_fixed_hedged", isRatio: false };
        case this.labels.differenceTwo:
          return { result: "v_exposed", isRatio: false };
        case this.labels.differenceThree:
          return { result: "v_budget_hedge_ratio", isRatio: false };
        default:
          return { result: "v_hedge_ratio", isRatio: false };
      }
    },
    getFuturesResult(differenceType) {
      switch (differenceType) {
        case this.labels.differenceOne:
          return { result: "v_fixed_hedged", isRatio: false };
        case this.labels.differenceTwo:
          return { result: "v_exposed", isRatio: false };
        case this.labels.differenceThree:
          return { result: "v_budget_hedge", isRatio: false };
        default:
          return { result: "v_hedge", isRatio: false };
      }
    },
    getBasisResult(differenceType) {
      switch (differenceType) {
        case this.labels.differenceOne:
          return { result: "v_fixed_hedged", isRatio: false };
        case this.labels.differenceTwo:
          return { result: "v_exposed", isRatio: false };
        case this.labels.differenceThree:
          return { result: "v_budget_basis", isRatio: false };
        default:
          return { result: "v_basis", isRatio: false };
      }
    },
    getRatioResult(differenceType) {
      switch (differenceType) {
        case this.labels.differenceOne:
          return { result: "v_fixed_hedged", isRatio: false };
        case this.labels.differenceTwo:
          return { result: "v_exposed", isRatio: false };
        case this.labels.differenceThree:
          return { result: "v_budget_ratio", isRatio: false };
        default:
          return { result: "v_ratio", isRatio: true };
      }
    },
    // Update user selection of Futures: Ratio: Basis in table
    updateTableInfo() {
      if (!this.isPrices) {
        const differences = [
          this.labels.difference,
          this.labels.differenceOne,
          this.labels.differenceTwo,
          this.labels.differenceThree,
        ];
        const tableQ = `tableq_${this.dataNest}`;

        // change nameData and Format in difference row
        let changeInfoTable = this.Analytics[tableQ].map((item) => {
          if (differences.includes(item.label)) {
            const { result, isRatio } = this.setRowDifference(item.label);
            const format = this.isFormatCurr(isRatio);
            return { ...item, nameData: result, formatF: format };
          }
          return item;
        });

        // Set quantity table
        this.$set(this.Analytics, tableQ, changeInfoTable);
        // Concat quantity table and prices table
        changeInfoTable = this.Analytics.tablep_zoomedData?.concat(
          this.Analytics.tableq_zoomedData,
        );
        this.$emit("changeInfoTable", changeInfoTable ? changeInfoTable : [{}]);
      }
    },
    // === configuration derivative
    allDerivatives() {
      return {
        ...this.getConfig({
          label: "Derivatives",
          colorBdr: "var(--textactive)",
          colorBck: "var(--textinactive)",
          show: "showAll",
          figureElements: "scatterImg",
          classNameIcon: "fa fa-eye",
        }),
      };
    },
    allDerivativesAve() {
      return {
        ...this.getConfig({
          label: "Ave",
          colorBdr: "var(--textactive)",
          colorBck: "var(--textinactive)",
          show: "showAve",
          figureElements: "bar",
          classNameIcon: "fa fa-eye",
        }),
      };
    },
    longfuture() {
      const objLF = {
        ...this.getConfig({
          label: "+Fwd/Fut",
          colorBdr: "var(--fwd)",
          colorBck: "var(--textinactive)",
          nameData: "p_long future",
          nameEvent: "p_longfuture",
          show: "showLFwd",
          figureElements: this.showAve ? "bar" : "scatterImg",
          classNameIcon: this.showAve ? "fa fa-minus" : "fa fa-plus",
          typeFigure: { nameFigure: "gBar", toZero: false },
          figureDetails: { barWidth: 0.75, strokeWidth: 2, height: 4 },
          xOffsetLeft: 0,
          showText: false,
          isDerivative: true,
        }),
      };
      return this.Analytics["rawData"]["existLFwd"] ? objLF : null;
    },
    shortfuture() {
      const objSF = this.getConfig({
        label: "-Fwd/Fut",
        colorBdr: "var(--fwd)",
        colorBck: "var(--textinactive)",
        nameData: "p_short future",
        nameEvent: "p_shortfuture",
        show: "showSFwd",
        figureElements: this.showAve ? "bar" : "scatterImg",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.75, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        isDerivative: true,
      });
      return this.Analytics["rawData"]["existSFwd"] ? objSF : null;
    },
    longcall() {
      const objLC = this.getConfig({
        label: "+Call",
        colorBdr: "var(--call)",
        colorBck: "var(--textinactive)",
        nameData: "p_long call",
        nameEvent: "p_longcall",
        show: "showLCall",
        figureElements: this.showAve ? "bar" : "scatterImg",
        classNameIcon: this.showAve ? "fa fa-minus" : "fa fa-chevron-up",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.75, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        isDerivative: true,
      });
      return this.Analytics["rawData"]["existLCall"] ? objLC : null;
    },
    shortcall() {
      const objSC = this.getConfig({
        label: "-Call",
        colorBdr: "var(--dark-call)",
        colorBck: "var(--textinactive)",
        nameData: "p_short call",
        nameEvent: "p_shortcall",
        show: "showSCall",
        classNameIcon: this.showAve ? "fa fa-minus" : "fa fa-chevron-down",
        figureElements: this.showAve ? "bar" : "scatterImg",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.75, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        isDerivative: true,
      });
      return this.Analytics["rawData"]["existSCall"] ? objSC : null;
    },
    longput() {
      const objLP = this.getConfig({
        label: "+Put",
        colorBdr: "var(--put)",
        colorBck: "var(--textinactive)",
        nameData: "p_long put",
        nameEvent: "p_longput",
        show: "showLPut",
        classNameIcon: this.showAve ? "fa fa-minus" : "fa fa-chevron-up",
        figureElements: this.showAve ? "bar" : "scatterImg",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.75, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        isDerivative: true,
      });
      return this.Analytics["rawData"]["existLPut"] ? objLP : null;
    },
    shortput() {
      const objSP = this.getConfig({
        label: "-Put",
        colorBdr: "var(--dark-put)",
        colorBck: "var(--textinactive)",
        nameData: "p_short put",
        nameEvent: "p_shortput",
        show: "showSPut",
        classNameIcon: this.showAve ? "fa fa-minus" : "fa fa-chevron-down",
        figureElements: this.showAve ? "bar" : "scatterImg",
        typeFigure: { nameFigure: "gBar", toZero: false },
        figureDetails: { barWidth: 0.75, strokeWidth: 2, height: 4 },
        xOffsetLeft: 0,
        showText: false,
        isDerivative: true,
      });
      return this.Analytics["rawData"]["existSPut"] ? objSP : null;
    },
    onHoverChart(val) {
      this.$emit("onTableFormat", val);
    },
    onUserShow(str) {
      this.$emit("eventOnUserShow", str);
    },
  },
  computed: {
    hasMultipleProperties() {
      const sum =
        this.Analytics["rawData"]["existLFwd"] +
        this.Analytics["rawData"]["existSFwd"] +
        this.Analytics["rawData"]["existLCall"] +
        this.Analytics["rawData"]["existSCall"] +
        this.Analytics["rawData"]["existLPut"] +
        this.Analytics["rawData"]["existSPut"];
      return sum > 1 && this.isPrices;
    },
    isCurrency() {
      return this.Analytics.assetType === "currency";
    },
    showAve() {
      return this.Analytics.userSelections.showAve;
    },
  },
};
</script>
